import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Flame, DollarSign, Leaf, Clock, Star, Package, Factory, Cpu, Wind } from 'lucide-react';
import Navbar from '../components/Navbar';
import UrgencyBanner from '../components/UrgencyBanner';
import Footer from '../components/Footer';
import { useCart } from '../context/CartContext';
import toast from 'react-hot-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, testimonialsRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/testimonials`),
      ]);
      setProducts(productsRes.data.slice(0, 3));
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product, 1);
    toast.success(`${product.name} added to cart!`);
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <UrgencyBanner />
      <Navbar />

      <section
        className="relative min-h-[80vh] flex items-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('https://customer-assets.emergentagent.com/job_eco-fuel-store/artifacts/rt1ky7hh_Gemini_Generated_Image_giw4xdgiw4xdgiw4.png')`,
        }}
      >
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-3xl">
            <div className="mb-4">
              <span className="text-xs font-mono tracking-widest uppercase text-lime-400 bg-lime-400/10 border border-lime-400/20 px-3 py-1.5 inline-block">
                ECO-FRIENDLY FUEL
              </span>
            </div>
            <h1 className="text-6xl md:text-8xl font-bold tracking-tighter uppercase leading-[0.9] mb-6 font-heading">
              SKIP THE <span className="text-lime-400">LPG QUEUE</span>
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-zinc-300 mb-8 max-w-2xl">
              90mm biomass briquettes = sawdust compressed into perfect cooking fuel. 
              3800kcal/kg energy cooks same as LPG but <strong className="text-lime-400">₹325/25kg bag</strong> beats ₹1100/cylinder!
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to="/products"
                className="bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1"
                data-testid="hero-shop-now-btn"
              >
                Shop Now <ArrowRight className="inline ml-2 w-5 h-5" />
              </Link>
              <Link
                to="/education"
                className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-8 py-4 transition-colors"
                data-testid="hero-learn-more-btn"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-zinc-950">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              Why <span className="text-lime-400">Briquettes</span>?
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              5 reasons Bengaluru is switching from LPG to biomass briquettes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            <div className="md:col-span-6 bg-zinc-900 border border-zinc-800 p-8 hover:border-lime-400/50 transition-colors group">
              <DollarSign className="w-12 h-12 text-lime-400 mb-4" />
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight font-heading uppercase mb-3">
                Saves 60%+
              </h3>
              <p className="text-zinc-300 leading-relaxed">
                ₹325/25kg bag vs LPG ₹1100/cylinder. 1 bag = 5 cylinders cooking power!
              </p>
            </div>

            <div className="md:col-span-6 bg-zinc-900 border border-zinc-800 p-8 hover:border-lime-400/50 transition-colors group">
              <Clock className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="text-2xl md:text-3xl font-semibold tracking-tight font-heading uppercase mb-3">
                Always Available
              </h3>
              <p className="text-zinc-300 leading-relaxed">
                No booking, no queues, same-day Bengaluru delivery. Never run out again.
              </p>
            </div>

            <div className="md:col-span-4 bg-zinc-900 border border-zinc-800 p-8 hover:border-lime-400/50 transition-colors group">
              <Flame className="w-12 h-12 text-lime-400 mb-4" />
              <h3 className="text-xl md:text-2xl font-semibold tracking-tight font-heading uppercase mb-3">
                Healthier
              </h3>
              <p className="text-zinc-300 leading-relaxed text-sm">
                70% less smoke vs wood, no CO leaks like faulty regulators.
              </p>
            </div>

            <div className="md:col-span-4 bg-zinc-900 border border-zinc-800 p-8 hover:border-lime-400/50 transition-colors group">
              <Leaf className="w-12 h-12 text-lime-400 mb-4" />
              <h3 className="text-xl md:text-2xl font-semibold tracking-tight font-heading uppercase mb-3">
                Eco-Friendly
              </h3>
              <p className="text-zinc-300 leading-relaxed text-sm">
                Made from sawdust/rice husk - zero trees cut. Waste-to-energy.
              </p>
            </div>

            <div className="md:col-span-4 bg-zinc-900 border border-zinc-800 p-8 hover:border-lime-400/50 transition-colors group">
              <Package className="w-12 h-12 text-orange-500 mb-4" />
              <h3 className="text-xl md:text-2xl font-semibold tracking-tight font-heading uppercase mb-3">
                Versatile
              </h3>
              <p className="text-zinc-300 leading-relaxed text-sm">
                Rice cooker, tandoor, pressure cooker, BBQ - all work perfectly.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-2">
                <span className="text-lime-400">Popular</span> Products
              </h2>
              <p className="text-zinc-400">Premium 90mm biomass briquettes</p>
            </div>
            <Link
              to="/products"
              className="text-zinc-400 hover:text-white uppercase tracking-wider text-sm transition-colors"
              data-testid="view-all-products"
            >
              View All <ArrowRight className="inline ml-1 w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors group relative overflow-hidden"
                data-testid={`product-card-${product.id}`}
              >
                {product.badge && (
                  <div className="absolute top-4 right-4 bg-orange-500 text-white px-2 py-1 text-xs font-bold uppercase tracking-widest">
                    {product.badge}
                  </div>
                )}
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-48 object-cover mb-4 border border-zinc-800"
                />
                <h3 className="text-2xl font-semibold tracking-tight font-heading uppercase mb-2">
                  {product.name}
                </h3>
                <p className="text-zinc-400 text-sm mb-4 line-clamp-2">
                  {product.description}
                </p>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-3xl font-bold text-lime-400">₹{product.price}</span>
                  {product.original_price && (
                    <span className="text-zinc-500 line-through">₹{product.original_price}</span>
                  )}
                </div>
                <div className="flex gap-2 mb-4 flex-wrap">
                  {product.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="bg-lime-400/10 text-lime-400 border border-lime-400/20 px-2 py-1 text-xs font-mono uppercase"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleAddToCart(product)}
                    className="flex-1 bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-6 py-3 transition-all hover:-translate-y-1"
                    data-testid={`add-to-cart-${product.id}`}
                  >
                    Add to Cart
                  </button>
                  <Link
                    to={`/products/${product.id}`}
                    className="bg-transparent border-2 border-zinc-700 text-white hover:border-lime-400 hover:text-lime-400 font-bold uppercase tracking-wider px-6 py-3 transition-colors"
                    data-testid={`view-details-${product.id}`}
                  >
                    Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-zinc-950">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4">
              What <span className="text-lime-400">Customers</span> Say
            </h2>
            <p className="text-zinc-400">Bengaluru businesses and homes love GreenPure Biofuels</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-zinc-900 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-lime-400 text-lime-400" />
                  ))}
                </div>
                <p className="text-zinc-300 leading-relaxed mb-4">"{testimonial.content}"</p>
                <div className="border-t border-zinc-800 pt-4">
                  <p className="font-semibold text-white">{testimonial.name}</p>
                  <p className="text-sm text-zinc-400">{testimonial.business_type}</p>
                  <p className="text-xs text-zinc-500 mt-1">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/testimonials"
              className="text-lime-400 hover:text-lime-300 uppercase tracking-wider text-sm font-bold transition-colors"
              data-testid="view-all-testimonials"
            >
              Read More Testimonials <ArrowRight className="inline ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-zinc-900 border-t border-zinc-800">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <span className="text-xs font-mono tracking-widest uppercase text-orange-500 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 inline-block mb-4">
                UPCOMING PROJECT
              </span>
              <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-2">
                Smart <span className="text-lime-400">Bio-CNG</span> Plant
              </h2>
              <p className="text-zinc-400 max-w-2xl">
                3 TPD facility in Chikkaballapur turning agricultural waste into 95%+ pure Bio-CNG
              </p>
            </div>
            <Link
              to="/bio-cng-plant"
              className="bg-lime-400 text-black hover:bg-lime-500 font-bold uppercase tracking-wider px-6 py-3 shadow-[4px_4px_0px_0px_#ffffff] transition-all hover:-translate-y-1 self-start md:self-auto"
              data-testid="bio-cng-explore-btn"
            >
              Explore Plant <ArrowRight className="inline ml-2 w-5 h-5" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link
              to="/bio-cng-plant"
              className="bg-zinc-950 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors group block"
              data-testid="bio-cng-card-feedstock"
            >
              <Factory className="w-10 h-10 text-lime-400 mb-4" />
              <h3 className="font-heading text-xl md:text-2xl font-bold uppercase mb-3 text-white">
                Waste to Fuel
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Agri-residues, cattle dung & organic waste processed via 7-stage anaerobic digestion.
              </p>
              <span className="text-lime-400 text-sm uppercase tracking-wider mt-4 inline-flex items-center group-hover:gap-2 gap-1 transition-all">
                Learn how <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            <Link
              to="/bio-cng-plant"
              className="bg-zinc-950 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors group block"
              data-testid="bio-cng-card-smart-tech"
            >
              <Cpu className="w-10 h-10 text-lime-400 mb-4" />
              <h3 className="font-heading text-xl md:text-2xl font-bold uppercase mb-3 text-white">
                Smart Automation
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                PLC/SCADA + IoT remote monitoring with predictive maintenance & real-time optimization.
              </p>
              <span className="text-lime-400 text-sm uppercase tracking-wider mt-4 inline-flex items-center group-hover:gap-2 gap-1 transition-all">
                See tech <ArrowRight className="w-4 h-4" />
              </span>
            </Link>

            <Link
              to="/bio-cng-plant"
              className="bg-zinc-950 border border-zinc-800 p-6 hover:border-lime-400/50 transition-colors group block"
              data-testid="bio-cng-card-impact"
            >
              <Wind className="w-10 h-10 text-lime-400 mb-4" />
              <h3 className="font-heading text-xl md:text-2xl font-bold uppercase mb-3 text-white">
                70-80% Lower Emissions
              </h3>
              <p className="text-zinc-400 text-sm leading-relaxed">
                Carbon-credit eligible under SATAT/GOBARdhan with zero-waste closed-loop model.
              </p>
              <span className="text-lime-400 text-sm uppercase tracking-wider mt-4 inline-flex items-center group-hover:gap-2 gap-1 transition-all">
                View impact <ArrowRight className="w-4 h-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-gradient-to-br from-lime-400 to-lime-500">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase font-heading mb-4 text-black">
            Ready to Switch?
          </h2>
          <p className="text-black text-lg mb-8 max-w-2xl mx-auto">
            Use code <strong className="font-mono">FIRSTBAG</strong> for your first 25kg bag at ₹325 with FREE delivery!
          </p>
          <Link
            to="/products"
            className="inline-block bg-black text-lime-400 hover:bg-zinc-900 font-bold uppercase tracking-wider px-8 py-4 shadow-[4px_4px_0px_0px_#000000] transition-all hover:-translate-y-1"
            data-testid="cta-order-now"
          >
            Order Now <ArrowRight className="inline ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
